// /src/pages/ContextSelectionPage.tsx
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Factory,
  Globe2,
  Loader2,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import type { AuthContextOption } from "@/lib/apiClient";

const contextMeta = {
  platform: {
    label: "Normex",
    icon: Globe2,
    description: "Administração global da plataforma",
    itemDescription: "Gestão global da Normex",
  },
  tenant: {
    label: "Empresas",
    icon: Building2,
    description: "Portais administrativos disponíveis para seu usuário",
    itemDescription: "Portal da Empresa",
  },
  client: {
    label: "Clientes",
    icon: Factory,
    description: "Operações de Clientes às quais você possui acesso direto",
    itemDescription: "Operação do Cliente",
  },
} as const;

function ContextCard({
  context,
  isSelecting,
  disabled,
  onSelect,
}: {
  context: AuthContextOption;
  isSelecting: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const meta = contextMeta[context.context_type];
  const Icon = meta.icon;
  const subtitle =
    context.context_type === "client" && context.tenant_name
      ? `${meta.itemDescription} • ${context.tenant_name}`
      : meta.itemDescription;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className="group flex w-full items-center gap-4 rounded-2xl border border-border/70 bg-card p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 sm:p-5"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-primary/15 bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
        <Icon className="h-5 w-5" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-semibold text-foreground">
          {context.context_name}
        </span>
        <span className="mt-1 block truncate text-sm text-muted-foreground">{subtitle}</span>
      </span>

      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
        {isSelecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
      </span>
    </button>
  );
}

export default function ContextSelectionPage() {
  const navigate = useNavigate();
  const {
    availableContexts,
    isContextSelectionRequired,
    isLoading,
    selectContext,
    cancelContextSelection,
    isAuthenticated,
  } = useAuth();
  const [selectingContextKey, setSelectingContextKey] = useState<string | null>(null);

  if (isAuthenticated) return <Navigate to="/" replace />;
  if (!isContextSelectionRequired) return <Navigate to="/login" replace />;

  const groups = (["platform", "tenant", "client"] as const)
    .map((type) => ({ type, items: availableContexts.filter((item) => item.context_type === type) }))
    .filter((group) => group.items.length > 0);

  async function choose(context: AuthContextOption) {
    setSelectingContextKey(context.context_key);
    try {
      await selectContext(context.context_key);
      toast.success(`Acesso selecionado: ${context.context_name}`);
      navigate("/");
    } catch (error) {
      setSelectingContextKey(null);
      toast.error(error instanceof Error ? error.message : "Não foi possível selecionar o acesso");
    }
  }

  function handleBack() {
    cancelContextSelection();
    navigate("/login");
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/[0.04] px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
      <div className="mx-auto w-full max-w-5xl">
        <div className="overflow-hidden rounded-3xl border border-border/70 bg-background/95 shadow-xl shadow-black/5 backdrop-blur">
          <header className="flex flex-col gap-6 border-b border-border/70 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/15 bg-primary/10 text-primary">
                <ShieldCheck className="h-7 w-7" />
              </div>
              <div>
                <img src="/logo-v3-preto.png" alt="Normex" className="h-8 w-auto dark:hidden" />
                <img src="/logo-v3-branco.png" alt="Normex" className="hidden h-8 w-auto dark:block" />
                <p className="mt-1 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Seleção de acesso
                </p>
              </div>
            </div>

            <Button variant="outline" onClick={handleBack} disabled={isLoading} className="self-start sm:self-auto">
              <LogOut className="mr-2 h-4 w-4" />
              Voltar ao login
            </Button>
          </header>

          <main className="px-5 py-7 sm:px-8 sm:py-9">
            <div className="max-w-2xl">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Escolha onde deseja entrar</h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground sm:text-base">
                Seu usuário possui mais de um acesso. Selecione o ambiente desta sessão; sua senha permanece única para todos eles.
              </p>
            </div>

            <div className="mt-8 space-y-8">
              {groups.map(({ type, items }) => {
                const meta = contextMeta[type];
                const Icon = meta.icon;

                return (
                  <section key={type} aria-labelledby={`context-section-${type}`}>
                    <div className="mb-3 flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div>
                        <h2 id={`context-section-${type}`} className="font-semibold text-foreground">
                          {meta.label}
                        </h2>
                        <p className="text-xs text-muted-foreground sm:text-sm">{meta.description}</p>
                      </div>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2">
                      {items.map((context) => (
                        <ContextCard
                          key={context.context_key}
                          context={context}
                          isSelecting={selectingContextKey === context.context_key}
                          disabled={isLoading || selectingContextKey !== null}
                          onSelect={() => void choose(context)}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
